import classNames from "classnames";
import { uniqueId } from "lodash";
import { Component } from "react";
import * as helpers from "./exportPanelBodyHelpers";
import * as toast from "./toast";
import { ExportFormatType } from "@app/exports/enumerations";
import { ReportPanelBodyBaseState } from "@app/exports/export-panel-body/reportPanelBodyBase";
import { ReportPanelBodyBaseProps } from "@app/exports/export-panel-body/reportPanelBodyBaseProps";
import { ExportNewProviderReportModel } from "@app/exports/exportModels";
import ExportService from "@app/exports/exportService";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import SharedUsers from "@app/reports/SharedUsers";
import NewProviderReportReportingPeriodNotice from "@app/exports/export-panel-body/NewProviderReportReportingPeriodNotice";

type NewProviderReportPanelBodyProps = ReportPanelBodyBaseProps & {
  projectId: number;
  runConfigurationId: number;
  isViewingPastRun?: boolean;
  isNoPublishedRun?: boolean;
  latestPublishedRunDate?: Date | null;
};

interface NewProviderReportPanelBodyState extends ReportPanelBodyBaseState {
  reportingPeriodDays: number;
  sharedUsers: number[];
}

const reportingPeriodOptions = [
  { key: 90, value: "Previous 90 Days" },
  { key: 180, value: "Previous 180 Days" },
  { key: 365, value: "Previous 365 Days" },
];

export default class NewProviderReportPanelBody extends Component<NewProviderReportPanelBodyProps, NewProviderReportPanelBodyState> {
  private readonly exportService = new ExportService();

  constructor(props: NewProviderReportPanelBodyProps) {
    super(props);

    this.state = this.getInitialBodyState();
  }

  private readonly reportNameId = uniqueId("new-provider-report-name-");
  private readonly emailId = uniqueId("new-provider-email-");

  public componentWillUnmount() {
    toast.dismiss();
  }

  public render() {
    const isFileNameOk = this.state.reportName && this.state.reportName.length > 0 && this.state.reportName.length < 255;
    const isLoading = this.state.loadingReportSettingsJSONStrings.length > 0;
    const { isViewingPastRun, isNoPublishedRun, latestPublishedRunDate } = this.props;

    return (
      <>
        <div className="row">
          <div className="col-12 col-md-4">
            <p className="lead mb0 demibold">Reporting Period</p>
            <div className="form-group row">
              <div className="col-lg-12">
                <QESSelect
                  className="mb-0"
                  containerClassName="mb-0"
                  label="Select reporting period:"
                  onChange={(e) => this.handleReportingPeriodChange(Number(e.target.value))}
                  testId="new-provider-report__reporting-period-select"
                  value={this.state.reportingPeriodDays.toString()}
                >
                  {reportingPeriodOptions.map(({ key, value }) => (
                    <option key={key} value={key}>{value}</option>
                  ))}
                </QESSelect>
              </div>
            </div>
          </div>
        </div>
        {!isViewingPastRun && !isNoPublishedRun && latestPublishedRunDate && (
          <div className="row">
            <div className="col-12">
              <NewProviderReportReportingPeriodNotice
                publishedRunDate={latestPublishedRunDate}
                reportingPeriodDays={this.state.reportingPeriodDays}
              />
            </div>
          </div>
        )}
        <hr />
        <div className="row">
          <div className="col-12">
            <div className={classNames("d-inline-flex align-items-start", { "has-error": !isFileNameOk })}>
              <div className="me-3">
                <label className="demibold" htmlFor={this.reportNameId}>
                  Report Name <span className="text-danger">*</span>
                </label>
                <QESTextInput
                  className=""
                  label=""
                  testId="new-provider-report__report-name-input"
                  id={this.reportNameId}
                  value={this.state.reportName}
                  onChange={(e) => this.handleReportNameChange(e.target.value)}
                  containerClassName="mb-0"
                />
              </div>
              <div>
                <label className="demibold">
                  File Format <span className="text-danger">*</span>
                </label>
                <QESSelect
                    className="mb-0"
                    containerClassName="mb-0"
                    onChange={(e) => this.handleOutputFormatChange(e.target.value as ExportFormatType)}
                    testId="export-new-provider-report__format-select"
                    value={this.state.outputFormat}
                    label=""
                >
                  <option value={ExportFormatType.CSV}>Comma-Delimited Text File (.csv)</option>
                  <option value={ExportFormatType.Tab}>Tab-Delimited Text File (.txt)</option>
                  <option value={ExportFormatType.Pipe}>Bar-Delimited Text File (.txt)</option>
                  <option value={ExportFormatType.Excel}>Microsoft Excel Workbook (.xlsx)</option>
                </QESSelect>
              </div>
            </div>
            {!isFileNameOk && (
              <label className="ms-2 text-danger"> Invalid report name, please enter a name between 1 and 255 characters.</label>
            )}
          </div>
        </div>
        {isFileNameOk && (
          <div className="row mt-2">
            <div className="col-12">
              <div className="d-inline-flex align-items-center">
                <SharedUsers
                  exportType="NewProviderReport"
                  customProjectIds={[this.props.projectId]}
                  tooltipTitle="Users in this list have been filtered to only show users with access to this project and information in this report"
                  networkAssessmentAnalysisType={this.props.initialNetworkAssessmentAnalysisType}
                  updateSelectedSharedUsers={this.handleSharedUsersChange}
                  sharedUsersFilter={this.state.sharedUsers}
                />
                <div className="d-flex align-items-center ms-2">
                  <QESCheckbox
                    containerClassName="demibold mb-0"
                    label={<>
                      <QesIcon iconName={QesIcons.MailV1} description="Mail" className="align-text-top me-2" />
                      Yes, please email me {(!this.state.sharedUsers || this.state.sharedUsers.length === 0) ? "" : `and the other user${this.state.sharedUsers.length > 1 ? "s" : ""}`} a link to the report when ready.
                    </>}
                    id={this.emailId}
                    className="me-2"
                    checked={this.state.emailOnSuccess}
                    onChange={(e) => this.handleEmailOnSuccessChange(e.target.checked)}
                    testId="new-provider-report__email-on-success"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="form-row align-items-center mt-3">
          <div className="col-12">
            {isLoading ? (
              <QESButton qesTestId="new-provider-report__generating" qesType="primary" disabled className="me-2">
                <QesIcon iconName={QesIcons.LoadingV1} description="Loading" className="qes-icon-spin align-text-top me-1" /> Generating...
              </QESButton>
            ) : (
              <QESButton
                qesTestId="new-provider-report__generate"
                qesType="primary"
                disabled={!isFileNameOk || isViewingPastRun || isNoPublishedRun}
                className="me-2"
                onClick={this.handleGenerateClick}
              >Generate</QESButton>
            )}
          </div>
        </div>
      </>
    );
  }

  private getInitialBodyState = (): NewProviderReportPanelBodyState => {
    return {
      emailOnSuccess: true,
      loadingReportSettingsJSONStrings: [],
      outputFormat: ExportFormatType.CSV,
      reportingPeriodDays: 90,
      reportName: helpers.reportName("NewProvider"),
      sharedUsers: [],
    };
  }

  private handleOutputFormatChange = (outputFormat: ExportFormatType) => {
    this.setState({ outputFormat });
  }

  private handleReportingPeriodChange = (reportingPeriodDays: number) => {
    this.setState({ reportingPeriodDays });
  }

  private handleGenerateClick = async () => {
    this.setState((prevState) => ({
      ...prevState,
      loadingReportSettingsJSONStrings: [...prevState.loadingReportSettingsJSONStrings, "generating"],
    }));

    try {
      const model: ExportNewProviderReportModel = {
        customProjectIds: [this.props.projectId],
        eMailOnSuccess: this.state.emailOnSuccess,
        exportName: this.state.reportName,
        formatType: this.state.outputFormat,
        reportingPeriodDays: this.state.reportingPeriodDays,
        runAsynchronous: true,
        sharedUsers: this.state.sharedUsers,
      };

      await this.exportService.postNewProviderReport(model);
      toast.toastInfo(this.state.reportName);
    } catch {
      toast.toastError(this.state.reportName);
    } finally {
      this.setState((prevState) => ({
        ...prevState,
        loadingReportSettingsJSONStrings: [] as string[],
      }));
    }
  }

  private handleEmailOnSuccessChange = (emailOnSuccess: boolean) => {
    this.setState({ emailOnSuccess });
  }

  private handleReportNameChange = (reportName: string) => {
    this.setState({ reportName });
  }

  private handleSharedUsersChange = (sharedUsers: number[]) => {
    this.setState({ sharedUsers });
  }
}
